/**
 * ClinicianResultPanel.tsx — the staff-facing verdict panel, now the Wave-4
 * AUTHORISE→RECOMMEND flow (UX Wave 4 §1, 2026-07-21; supersedes the old
 * "Build a plan / Email the plan link" stacked buttons — the clinician-led
 * plan-building lane retired with it: terms are the PATIENT's choice).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 4.
 *
 * The flow, for a SUBMITTED eligible/clinician_review assessment:
 *   1. VIEW — LEADS with the ELIGIBILITY RESULT (UX Wave 9 §3: the submit CTA
 *      reads "Check eligibility", so the verdict must be the headline — a
 *      prominent result block naming Eligible / Needs clinician review BEFORE
 *      anything else; a not_eligible form never reaches this panel, it greys
 *      out in the form itself), then the read-only questionnaire (rendered by
 *      the caller through `renderQuestionnaire`, which also slots the
 *      per-answer NOTE affordance under every question — Wave 4 §4
 *      notes-per-answer) with ONE bottom-right CTA: "Authorise
 *      questionnaire". An existing recommendation shows as a banner
 *      (authorising again RE-ISSUES it).
 *   2. RECOMMEND — multi-select of the clinic's medications from the plan
 *      options read (MONEY-FREE labels: name + form only — this is a
 *      clinician surface, the money-redaction guard scans it; the options
 *      payload's prices are never rendered), the structured clinical-notes
 *      area (the assessment-notes store) and the submitted weight photo
 *      (the existing signed READ — stub-safe), then ONE confirm that POSTs
 *      the recommendation set (upsert-latest) and then APPROVES the plan.
 * The server 409s unless the assessment is submitted + eligible/
 * clinician_review — the 409 detail is surfaced verbatim (parseError).
 *
 * ── ⚠️ THE CONFIRM APPROVES; IT DOES NOT "SEND AN EMAIL" (2026-09-07) ─────────
 * It used to POST the recommendation and then call `sendPlanHandover`, so the
 * only thing this button could ever do was email a plan link. The assessment is
 * submitted for clinical review BEFORE checkout, so a patient who had ALREADY
 * PAID was sent back to a checkout they had finished. The confirm now posts the
 * clinician's APPROVAL (`approveClinicianPlan`) and the SERVER decides what that
 * means: capture the card already authorised for exactly this plan and dispatch
 * it, or email the plan link exactly as before.
 *
 * ⚠️ THIS SCREEN DOES NOT CHOOSE THE LANE AND CANNOT (R41). It sends one thing
 * and renders the answer. There is no branch here that could ask for a charge,
 * no argument that names a medication (the server reads the recommendation it
 * just stored), and no state that could be edited into one.
 *
 * ⚠️ AND IT MUST RENDER THE OUTCOMES DIFFERENTLY. Saying "recommendations
 * emailed to the patient" for a patient we have just charged is a claim about
 * their money that is not true — and saying their prescription has gone to the
 * pharmacy when only the CHARGE is guaranteed is a claim about their treatment
 * that is not true either. Both live in `approvalNotice` below, which is where
 * the wording rules are written down.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  addAssessmentNote,
  approveClinicianPlan,
  getAssessmentNotes,
  getAssessmentRecommendation,
  getAssessmentReview,
  saveAssessmentRecommendation,
} from '../api/clinic';
import { getPlanOptions } from '../api/prices';
import type {
  ApprovePlanResult,
  AssessmentRecommendation,
  AssessmentReview,
  AssessmentSubmitResult,
  CaseNote,
  PlanOption,
  QuestionPublic,
} from '../api/types';
import { formatUtcDate } from '../lib/format';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { NoteEditor } from '../admin/callbacks/NoteEditor';
import { SectionTitle } from '../staff/ui';
import { Alert, Button, Card, CheckboxRow, ErrorState, Help, Spinner } from '../ui/kit';
import { SafetySignoffNotice } from './SafetySignoffNotice';
import { AssessmentNotesSection } from './AssessmentNotes';

/** The server allows 1..4 recommended productKeys — mirrored as a UI gate. */
const MAX_RECOMMENDED = 4;

type Loadable<T> = { state: 'loading' | 'ready' | 'error'; data: T | null };

/**
 * THE OUTCOME COPY — what the clinician is told, chosen ONLY from what the
 * server said happened.
 *
 * ⚠️ THREE SITUATIONS, THREE SENTENCES, AND THE HARD RULE IS THAT NO SENTENCE
 * MAY CLAIM MORE THAN THE SERVER GUARANTEED.
 *
 *  1. `charged` + `dispatch:'sent'` — the money moved AND the pharmacy confirmed
 *     it has the prescription. This is the only case in which "gone to the
 *     pharmacy" is a true sentence, and it is the only case that gets a tick.
 *
 *  2. `charged` + `dispatch:'pending'` — THE ONE THIS FUNCTION EXISTS FOR. The
 *     money moved and the pharmacy step did not confirm. orders-service issues
 *     the prescription and posts it POST-COMMIT and best-effort, deliberately
 *     swallowing faults there so that nothing can unwind a banked charge; the
 *     consequence is that a capture can succeed and everything after it fail,
 *     and `approve()` still returns normally. This screen used to render that
 *     as "Approved and dispatched… their prescription has gone to the pharmacy"
 *     with a green tick, which is a false statement about a patient's treatment
 *     made at the one moment somebody could still act on it. It now says the
 *     charge landed, says the pharmacy has NOT been confirmed, and asks for the
 *     order to be checked — under a WARNING tone, because it is not done.
 *
 *  3. `plan_link_sent` — an email went out. `gate_refused` is called out
 *     separately because that patient HAD authorised payment and only an
 *     outstanding check stopped the charge; the plain line would be true and
 *     would still leave the clinician believing they were finished.
 *
 * ⚠️ NOTHING HERE INFERS. There is no `dispatch ?? 'sent'` and no "charged
 * therefore dispatched": an answer that does not say `sent` is treated as not
 * sent, in every case, including one this build has not heard of. The cost of
 * being wrong that way is a wasted click; the cost of being wrong the other way
 * is a paid patient who never receives their medication.
 *
 * MONEY-FREE (Reshape §3.1): an order reference is not a price, and no sentence
 * here carries an amount.
 */
export function approvalNotice(outcome: ApprovePlanResult): {
  tone: 'success' | 'warning';
  text: string;
} {
  const ref = outcome.orderReference ? ` (${outcome.orderReference})` : '';
  if (outcome.outcome === 'charged') {
    return outcome.dispatch === 'sent'
      ? {
          tone: 'success',
          text: `Approved and charged — the patient had already authorised payment, and their prescription has gone to the pharmacy${ref}. No email was sent; there is nothing further for them to do.`,
        }
      : {
          tone: 'warning',
          text: `Approved and charged — the patient had already authorised payment${ref}, so no email was sent. Their prescription has NOT reached the pharmacy yet: open their order and check it has been issued and sent before you move on.`,
        };
  }
  if (outcome.reason === 'gate_refused') {
    // ⚠️ THE ONE LINK-LANE REASON THAT NEEDS SAYING OUT LOUD. This patient HAD
    // authorised payment, and the only thing that stopped the charge was an
    // outstanding check on their order (the weight photo still needing
    // attestation, or an authorisation Stripe has let lapse). The plain
    // "emailed" line would be true but would leave the clinician believing they
    // were finished, and the patient would get a plan link for a checkout they
    // completed. Deliberately does NOT name which check: the reason enum is
    // PHI-free and coarse on purpose, and inventing a specific remedy here would
    // be this screen asserting something the server did not tell it.
    return {
      tone: 'warning',
      text: 'Personalised recommendations emailed to the patient. Note: they had already authorised payment, but an outstanding check on their order stopped it completing here — open their order to finish it.',
    };
  }
  return {
    tone: 'success',
    text: 'Personalised recommendations emailed to the patient — they choose from your recommended treatments and pick their own plan length.',
  };
}

export function ClinicianResultPanel({
  result,
  clinicSlug,
  renderQuestionnaire,
}: {
  result: AssessmentSubmitResult;
  clinicSlug: string;
  /**
   * The read-only questionnaire view (VIEW stage). The panel hands back the
   * per-question anchored-note control so the caller can slot it under each
   * answer — since the 2026-08-27 drawer restructure that caller is the
   * ANSWER SUMMARY (QuestionnaireSummary → StructuredQuestionnaire's
   * `renderItemExtra`), whose items carry only key + prompt, so the control
   * takes exactly that Pick (a full QuestionPublic still satisfies it).
   * Absent on a just-submitted form (the drawer reload brings the full view).
   */
  renderQuestionnaire?: (
    noteControl: (question: Pick<QuestionPublic, 'key' | 'prompt'>) => ReactNode,
  ) => ReactNode;
}) {
  const [stage, setStage] = useState<'view' | 'recommend'>('view');
  const [options, setOptions] = useState<Loadable<PlanOption[]>>({ state: 'loading', data: null });
  const [recommendation, setRecommendation] = useState<AssessmentRecommendation | null>(null);
  const [review, setReview] = useState<Loadable<AssessmentReview>>({ state: 'loading', data: null });
  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  // `warning` joined the pair on 2026-09-07: a capture whose pharmacy step did
  // not confirm is neither a success nor a failure — the money moved, and a
  // human has to finish the order. A green tick over that sentence is the whole
  // problem, so the tone carries the difference as well as the words.
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning' | 'danger'; text: string } | null>(null);

  // The clinic's medication options — the recommend multi-select's source AND
  // the name resolver for the recommendation-on-file banner. The payload
  // carries prices; ONLY name/form are ever rendered here (money-free rule).
  const loadOptions = useCallback(() => {
    setOptions({ state: 'loading', data: null });
    getPlanOptions(clinicSlug)
      .then((response) => setOptions({ state: 'ready', data: response.options }))
      .catch(() => setOptions({ state: 'error', data: null }));
  }, [clinicSlug]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // The stored recommendation set (null until one is issued) — best-effort:
  // a failed read just means no banner/preselection, never a broken panel.
  useEffect(() => {
    let cancelled = false;
    getAssessmentRecommendation(result.assessmentId)
      .then((read) => {
        if (!cancelled) setRecommendation(read.recommendation);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [result.assessmentId]);

  // The anchored-notes source for the per-answer affordance — only fetched
  // when a questionnaire view exists to hang the notes off.
  const loadNotes = useCallback(() => {
    if (!renderQuestionnaire) return;
    getAssessmentNotes(result.assessmentId)
      .then((list) => setNotes(list.items))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- renderQuestionnaire is a render prop, stable per mount
  }, [result.assessmentId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // The weight photo rides the existing structured review read (signed READ
  // url; null in stub mode) — fetched once, when the recommend stage opens.
  useEffect(() => {
    if (stage !== 'recommend' || review.state === 'ready') return;
    let cancelled = false;
    getAssessmentReview(result.assessmentId)
      .then((r) => {
        if (!cancelled) setReview({ state: 'ready', data: r });
      })
      .catch(() => {
        if (!cancelled) setReview({ state: 'error', data: null });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- review.state guards the one-shot fetch
  }, [stage, result.assessmentId]);

  const needsReview = result.eligibility === 'clinician_review';

  /** Resolve productKeys → medication names (fall back to the raw key). */
  function medicationNames(productKeys: string[]): string {
    return productKeys
      .map((key) => options.data?.find((o) => o.productKey === key)?.medicationName ?? key)
      .join(' · ');
  }

  function openRecommend() {
    setSelected(recommendation?.productKeys ?? []);
    setNotice(null);
    setStage('recommend');
  }

  function toggle(productKey: string) {
    setSelected((prev) =>
      prev.includes(productKey) ? prev.filter((k) => k !== productKey) : [...prev, productKey],
    );
  }

  /**
   * ONE confirm: store the recommendation set, then APPROVE the plan.
   *
   * ⚠️ THE ORDER IS LOAD-BEARING. The approval is what the server reads the
   * recommendation FOR — it compares the medications the clinician approved
   * against the plan the patient authorised — so the set has to be stored
   * first. If the save throws, the approval never runs and nothing has
   * happened; if the approval throws, the set is stored and the clinician can
   * retry (both writes are upserts/idempotent).
   *
   * ⚠️ AND THE SECOND CALL PASSES NO PLAN AND NO TERM. Everything the decision
   * needs, the server already holds: the recommendation it has just stored and
   * the authorisation orders-service owns. There is deliberately nothing here
   * that could name a medication or ask for a charge (R41).
   *
   * ⚠️ WHAT IT DOES PASS IS THE ID OF THE ROW IT JUST SAVED, and that is not a
   * plan — it is an optimistic-concurrency token (2026-09-07). Because this is
   * two calls, a second clinician working the same assessment can replace the
   * recommendation in the gap, and the server (which reads the CURRENT row)
   * would then capture and dispatch THEIR medication list under THIS
   * clinician's name. Handing back the id makes the server refuse instead. It
   * can only ever cause a 409: the server compares, it never looks the id up.
   */
  async function confirmAndApprove() {
    if (sending || selected.length === 0) return;
    setSending(true);
    setNotice(null);
    try {
      const saved = await saveAssessmentRecommendation(result.assessmentId, selected);
      // The APPROVAL. The server routes it: capture the card the patient already
      // authorised for exactly this plan and send the prescription to the
      // pharmacy, or email the plan link — see approveClinicianPlan. The id of
      // the row we just saved rides along so the approval cannot land on a set
      // somebody else replaced it with (see the ⚠️ above); an older server that
      // does not return one simply gets the unguarded call.
      const outcome = await approveClinicianPlan(result.assessmentId, {
        recommendationId: saved.recommendationId,
      });
      setRecommendation({ productKeys: selected });
      setStage('view');
      setNotice(approvalNotice(outcome));
    } catch (err) {
      const conflict = err instanceof ApiError && err.status === 409;
      // ⚠️ 503 IS ITS OWN SENTENCE, AND IT HAS TO BE. The approval route fails
      // CLOSED when orders-service cannot be reached, because a lost response may
      // be a capture that happened. "Please try again" is right, but a clinician
      // also needs to know that nothing was emailed — otherwise they leave the
      // screen assuming the patient has been contacted.
      const unreachable = err instanceof ApiError && err.status === 503;
      setNotice({
        tone: 'danger',
        // ⚠️ NOTHING HERE MAY CLAIM THAT NO PAYMENT WAS TAKEN. On a 503 or a 5xx
        // the request may have CAPTURED and lost its response, and this screen
        // cannot know. "Could not CONFIRM" is the strongest true statement; what
        // we can say without qualification is that no email went out, and that a
        // retry is safe (the server's approve is idempotent — it either captures
        // or finishes a capture that already happened). A cheerful "no payment
        // was taken" would be wrong in exactly the case that matters most.
        text: unreachable
          ? "We couldn't confirm the approval — no email was sent to the patient. Please try again; retrying is safe and will complete the order."
          : conflict && err.detail
            ? `We couldn't complete the approval — ${err.detail}`
            : conflict
              ? "We couldn't complete the approval — the form must be submitted and eligible, and the patient needs an email on file."
              : 'Sorry — we could not confirm the approval. Please try again; retrying is safe.',
      });
    } finally {
      setSending(false);
    }
  }

  /** The per-answer anchored-note affordance handed to the questionnaire view. */
  const noteControl = (question: Pick<QuestionPublic, 'key' | 'prompt'>): ReactNode => (
    <AnswerNotes
      question={question}
      notes={notes.filter((n) => n.questionKey === question.key)}
      onAdd={async (html) => {
        await addAssessmentNote(result.assessmentId, html, question.key);
        loadNotes();
      }}
    />
  );

  if (stage === 'recommend') {
    return (
      <Card>
        <SectionTitle>Recommend treatment</SectionTitle>
        <Help>
          Pick one or more treatments to recommend — the patient sees only your recommended options
          and chooses their own plan length. If they have already paid for one of these, approving
          completes their order and sends it to the pharmacy instead of emailing them again.
        </Help>

        {notice ? (
          <Alert tone={notice.tone} role={notice.tone === 'danger' ? 'alert' : 'status'} style={{ marginBottom: 'var(--gps-s-3)' }}>
            {notice.text}
          </Alert>
        ) : null}

        {options.state === 'loading' ? (
          <Spinner label="Loading the treatment options…" />
        ) : options.state === 'error' || !options.data || options.data.length === 0 ? (
          <ErrorState message="Sorry — we could not load the treatment options." onRetry={loadOptions} />
        ) : (
          <>
          {/* Contract obligation (contracts v0.9.0): a consumer MUST surface
              `clinicallyApproved: false` to staff. A clinician recommending a
              treatment here needs to know the patient's risk wording is not yet
              ratified, so they cover it themselves rather than assume. */}
          <SafetySignoffNotice options={options.data} />
          <div role="group" aria-label="Recommended treatments" style={{ display: 'grid', gap: '2px', marginBottom: 'var(--gps-s-3)' }}>
            {options.data.map((option) => (
              <CheckboxRow
                key={option.productKey}
                label={`${option.medicationName} (${option.form})`}
                checked={selected.includes(option.productKey)}
                // Mirror the server's 1..4 gate: at 4, unchecked rows lock.
                disabled={sending || (!selected.includes(option.productKey) && selected.length >= MAX_RECOMMENDED)}
                onChange={() => toggle(option.productKey)}
              />
            ))}
          </div>
          </>
        )}

        <WeightPhotoBlock review={review} />

        {/* The structured clinical-notes area — the assessment-notes store
            (anchored notes show their question marker inside the section). */}
        <div style={{ margin: 'var(--gps-s-3) 0' }}>
          <AssessmentNotesSection assessmentId={result.assessmentId} />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            type="button"
            variant="ghost"
            disabled={sending}
            onClick={() => setStage('view')}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            Back to the questionnaire
          </Button>
          <Button
            type="button"
            variant="cta"
            disabled={sending || selected.length === 0}
            onClick={() => void confirmAndApprove()}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            {/* ⚠️ NOT "Confirm and email the patient" any more (2026-09-07). The
                button no longer always emails: for a patient who has already
                authorised payment for exactly this plan the server captures and
                dispatches instead. A label that names one of the two lanes is
                wrong half the time, and wrong in the direction that matters —
                so it names the DECISION, which is true either way, and the
                notice afterwards says what actually happened. */}
            {sending ? 'Approving…' : 'Confirm and approve'}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      {notice ? (
        <Alert tone={notice.tone} role={notice.tone === 'danger' ? 'alert' : 'status'}>
          {notice.text}
        </Alert>
      ) : null}

      {/* UX Wave 9 §3 — the ELIGIBILITY RESULT leads, prominently, before the
          recommend step: the CTA said "Check eligibility", so the verdict is
          the headline, not a sentence in a banner. */}
      <section
        aria-label="Eligibility result"
        role="status"
        style={{
          border: `1.5px solid ${needsReview ? 'var(--gps-border-strong)' : 'var(--gps-primary)'}`,
          borderRadius: 'var(--gps-r-md)',
          padding: 'var(--gps-s-4)',
          background: needsReview ? 'var(--gps-surface-2)' : 'var(--gps-primary-light)',
        }}
      >
        <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
          Eligibility result
        </p>
        <p
          style={{
            margin: '4px 0 6px',
            fontFamily: 'var(--gps-font-display)',
            fontSize: '26px',
            color: 'var(--gps-green-text)',
          }}
        >
          {needsReview ? 'Needs clinician review' : 'Eligible'}
        </p>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--gps-text)' }}>
          {needsReview
            ? 'The answers flag this form for a clinical judgement — check the answers below, then authorise the questionnaire to recommend treatment.'
            : 'The answers meet the eligibility rules — check them below, then authorise the questionnaire to recommend treatment.'}
        </p>
      </section>

      {recommendation ? (
        <Alert tone="info" role="status">
          Recommendation on file: <strong>{medicationNames(recommendation.productKeys)}</strong>. Authorising
          again re-issues it and re-emails the patient. Copy or re-send the plan link itself from the
          Overview tab.
          {/* ⚠️ THE PLAN-LINK CONTROLS ARE DELIBERATELY NOT IN THIS BANNER
              (CTO 2026-09-05). A clinician still needs the link — the CTO's
              2026-09-04 reversal that put it on this surface has NOT been
              undone — but the Questionnaire tab is for reading the patient's
              answers, so both roles now reach it in ONE place at the foot of
              Overview. The sentence above points there rather than duplicating
              a control two tabs apart, which is how the two of them drift. */}
        </Alert>
      ) : null}

      {renderQuestionnaire ? renderQuestionnaire(noteControl) : null}

      {/* ONE bottom-right CTA (Wave 4 §1) — never two stacked buttons. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="button"
          variant="cta"
          onClick={openRecommend}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
        >
          Authorise questionnaire
        </Button>
      </div>
    </div>
  );
}

/**
 * The submitted weight photo inside the recommend stage — the SAME rules as
 * the review's photo card: signed `photoUrl` renders; `photoRef` with no URL
 * (stub-mode storage) shows the plain note; neither = "none on file".
 */
function WeightPhotoBlock({ review }: { review: Loadable<AssessmentReview> }) {
  return (
    <div style={{ margin: 'var(--gps-s-3) 0' }}>
      <SectionTitle>Weight photo</SectionTitle>
      {review.state === 'loading' ? (
        <Spinner label="Loading the weight photo…" />
      ) : review.data?.photoUrl ? (
        <>
          <img
            src={review.data.photoUrl}
            alt="Patient's submitted weight photo"
            style={{ maxWidth: '320px', width: '100%', display: 'block', borderRadius: 'var(--gps-r-md)', border: '1px solid var(--gps-border)' }}
          />
          <p style={{ color: 'var(--gps-muted)', fontSize: '12px', margin: 'var(--gps-s-2) 0 0' }}>
            This link is time-limited and will expire — it is not a permanent copy.
          </p>
        </>
      ) : review.data?.photoRef ? (
        <Help style={{ marginBottom: 0 }}>Photo on file — preview unavailable on this environment</Help>
      ) : (
        <Help style={{ marginBottom: 0 }}>No weight photo on file.</Help>
      )}
    </div>
  );
}

/**
 * The small per-answer note affordance (Wave 4 §4): a low-prominence toggle
 * under the answer opens the anchored notes (oldest→newest) + an add editor
 * that POSTs with this questionKey. Bodies are sanitised AGAIN before
 * rendering (defence in depth — same rule as every note surface).
 */
function AnswerNotes({
  question,
  notes,
  onAdd,
}: {
  question: Pick<QuestionPublic, 'key' | 'prompt'>;
  notes: CaseNote[];
  onAdd: (html: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const label = notes.length > 0 ? `Notes (${notes.length})` : 'Add note';
  return (
    <div style={{ marginTop: '6px' }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${label} — ${question.prompt}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none',
          border: 0,
          color: 'var(--gps-link)',
          font: 'inherit',
          fontSize: '13px',
          cursor: 'pointer',
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        {label}
      </button>
      {open ? (
        <div
          style={{
            border: '1px solid var(--gps-border)',
            borderRadius: 'var(--gps-r-md)',
            padding: 'var(--gps-s-3)',
            marginTop: '6px',
            background: 'var(--gps-surface-2)',
          }}
        >
          {notes.length === 0 ? (
            <Help>No notes on this answer yet.</Help>
          ) : (
            <ul aria-label={`Notes on ${question.prompt}`} style={{ listStyle: 'none', margin: '0 0 var(--gps-s-2)', padding: 0, display: 'grid', gap: '8px' }}>
              {[...notes]
                .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                .map((note) => (
                  <li key={note.noteId} style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-sm)', padding: '6px 10px', background: 'var(--gps-surface)' }}>
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--gps-muted)', marginBottom: '4px' }}>
                      {formatUtcDate(note.createdAt)}
                      {note.editedAt ? ' · edited' : ''}
                    </span>
                    {/* Defence in depth — strip before rendering as HTML. */}
                    <div style={{ fontSize: '13.5px', color: 'var(--gps-text)' }} dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.body) }} />
                  </li>
                ))}
            </ul>
          )}
          <NoteEditor
            formLabel={`Note on: ${question.prompt}`}
            textboxLabel={`New note on ${question.prompt}`}
            submitLabel="Save note"
            onSubmit={onAdd}
          />
        </div>
      ) : null}
    </div>
  );
}
